import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CardContent, CircularProgress, Typography } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { wealthService, apiErrorMessage } from '../../utils/api';
import { formatLakhCr } from './format';
import { whenFamilySaved } from './familySave';

const ICONS = {
  MUTUAL_FUND: <BarChartIcon />,
  EQUITY: <ShowChartIcon />,
  NPS: <SavingsOutlinedIcon />,
  BANK: <AccountBalanceOutlinedIcon />,
};

/**
 * After the fetch: total wealth, then each asset type split by person. Next continues to the
 * manual cards, pre-filled with these numbers, to add what could not be fetched.
 */
function FamilyWealth() {
  const [family, setFamily] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      await whenFamilySaved();
      setFamily(await wealthService.getFamilySummary());
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load your family wealth.'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <CardContent>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error}</Alert>
      </CardContent>
    );
  }
  if (!family) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <CardContent>
      <Typography variant="h6" sx={{ color: 'primary.main' }}>Your Total Investments</Typography>
      <Typography variant="h4" component="p" align="center" sx={{ fontWeight: 600, my: 2 }}>
        {formatLakhCr(family.total_value)}
      </Typography>

      {family.by_category.length === 0 && (
        <Typography variant="body1" sx={{ fontSize: '0.95rem', lineHeight: 1.6 }}>
          Nothing was fetched. Click Next to enter your investments, or go Back to fetch them.
        </Typography>
      )}

      {family.by_category.map((cat) => (
        <Box key={cat.key} component="section" aria-label={cat.label} sx={{ py: 1, borderBottom: '1px dashed #d0d0d0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.25 }}>
            <Box aria-hidden="true" sx={{ display: 'flex' }}>{ICONS[cat.key]}</Box>
            <Typography variant="subtitle1" sx={{ flex: 1 }}>{cat.label}</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{formatLakhCr(cat.total_value)}</Typography>
          </Box>
          {cat.people.map((p) => (
            <Box key={p.label} sx={{ display: 'flex', justifyContent: 'space-between', pl: 4.5 }}>
              <Typography variant="body2" color="text.secondary">{p.label}</Typography>
              <Typography variant="body2">{formatLakhCr(p.value)}</Typography>
            </Box>
          ))}
        </Box>
      ))}

      {family.by_category.length > 0 && (
      <Typography variant="body1" sx={{ mt: 2.5, fontSize: '0.95rem', lineHeight: 1.6 }}>
        We have pre-filled your Stocks, MFs, ETFs, Bank Balances and NPS.
        <br />
        Now add your other investments such as global, ESOP &amp; RSU, etc.
      </Typography>
      )}
    </CardContent>
  );
}

export default FamilyWealth;
